import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ShoppingCartIcon, ArrowLeftIcon, CheckCircleIcon, Loader2Icon, TrashIcon, InfoIcon } from 'lucide-react';
import { useCart, CartItem } from '../components/cart/CartContext';
import { useDemoPurchases } from '../hooks/useDemoPurchases';
export const CheckoutPage = () => {
  const navigate = useNavigate();
  const {
    items,
    totalPrice,
    totalItems,
    removeItem,
    clearCart
  } = useCart();
  const {
    addPurchase
  } = useDemoPurchases();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Snapshot of purchased items (captured before cart is cleared)
  const purchasedItemsRef = useRef<CartItem[]>([]);
  const purchasedTotalRef = useRef(0);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleCompletePurchase = () => {
    setIsProcessing(true);
    // Snapshot cart before clearing
    purchasedItemsRef.current = [...items];
    purchasedTotalRef.current = totalPrice;
    // Simulate processing delay
    setTimeout(() => {
      // Save demo purchase to localStorage
      addPurchase(items.map(item => ({
        id: item.id,
        title: item.title,
        price: item.price,
        image: item.image,
        quantity: item.quantity
      })), totalPrice);
      clearCart();
      setIsProcessing(false);
      setOrderComplete(true);
    }, 1800);
  };
  // Empty cart state (only when no order was just completed)
  if (items.length === 0 && !orderComplete) {
    return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-4386">
        <Navbar scrolled={scrolled} data-id="element-4387" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-4388">
          <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm" data-id="element-4389">
            <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4390">
              <ShoppingCartIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-4391" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4392">
              Your cart is empty
            </h2>
            <p className="text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-4393">
              Add some products to your cart before checking out.
            </p>
            <button onClick={() => navigate('/browse')} className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-4394">
              Browse Products
            </button>
          </div>
        </main>
        <Footer data-id="element-4395" />
      </div>;
  }
  // Order confirmed — demo success screen
  if (orderComplete) {
    const purchasedItems = purchasedItemsRef.current;
    const purchasedTotal = purchasedTotalRef.current;
    return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-4396">
        <Navbar scrolled={scrolled} data-id="element-4397" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-4398">
          {/* Success card */}
          <div className="bg-white dark:bg-[#162825] rounded-xl p-10 text-center shadow-sm" data-id="element-4399">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-4400">
              <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" data-id="element-4401" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-4402">
              Order Confirmed!
            </h2>
            <p className="text-gray-500 dark:text-[#7A8F8A] mb-8 max-w-md mx-auto" data-id="element-4403">
              Thank you for your purchase. Your order has been placed
              successfully.
            </p>

            {/* Order summary */}
            {purchasedItems.length > 0 && <div className="bg-gray-50 dark:bg-[#0F1F1D] rounded-lg p-5 mb-8 text-left max-w-md mx-auto" data-id="element-4404">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-[#B0C1BD] uppercase tracking-wide mb-3" data-id="element-4405">
                  Order Summary
                </h3>
                <div className="space-y-3" data-id="element-4406">
                  {purchasedItems.map(item => <div key={item.id} className="flex justify-between items-center text-sm" data-id="element-4407">
                      <span className="text-gray-700 dark:text-[#F8FAF9] truncate mr-4" data-id="element-4408">
                        {item.title}
                        {item.quantity > 1 && <span className="text-gray-400 dark:text-[#7A8F8A]" data-id="element-4409">
                            {' '}
                            × {item.quantity}
                          </span>}
                      </span>
                      <span className="text-gray-800 dark:text-[#F8FAF9] font-medium flex-shrink-0" data-id="element-4410">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>)}
                </div>
                <div className="border-t border-gray-200 dark:border-[#1A3330] mt-3 pt-3 flex justify-between" data-id="element-4411">
                  <span className="text-sm font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4412">
                    Total
                  </span>
                  <span className="text-sm font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4413">
                    ${purchasedTotal.toFixed(2)}
                  </span>
                </div>
              </div>}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-4414">
              <button onClick={() => navigate('/buyer-dashboard?tab=purchases')} className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-4415">
                View My Purchases
              </button>
              <button onClick={() => navigate('/browse')} className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#F8FAF9] bg-white dark:bg-transparent rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-4416">
                Continue Shopping
              </button>
            </div>
          </div>

          {/* Demo info banner */}
          <div className="mt-4 flex items-start gap-3 bg-gray-100 dark:bg-[#162825] rounded-lg px-5 py-4 max-w-3xl mx-auto" data-id="element-4417">
            <InfoIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A] mt-0.5 flex-shrink-0" data-id="element-4418" />
            <p className="text-xs text-gray-500 dark:text-[#7A8F8A] leading-relaxed" data-id="element-4419">
              This is a demo transaction. Connect your preferred payment
              provider (Stripe, Paddle, or others) to enable real payments.
            </p>
          </div>
        </main>
        <Footer data-id="element-4420" />
      </div>;
  }
  // Main checkout view
  return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-4421">
      <Navbar scrolled={scrolled} data-id="element-4422" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-4423">
        {/* Back button */}
        <button onClick={() => navigate(-1)} className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD] mb-8 transition-colors" data-id="element-4424">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-4425" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8" data-id="element-4426">
          Checkout
        </h1>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-4427">
          {/* Order Items */}
          <div className="flex-1" data-id="element-4428">
            <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm overflow-hidden" data-id="element-4429">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1A3330]" data-id="element-4430">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4431">
                  Order Items ({totalItems})
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-[#1A3330]" data-id="element-4432">
                {items.map(item => <div key={item.id} className="flex items-center gap-4 px-6 py-4" data-id="element-4433">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-[#1A3330] flex-shrink-0" data-id="element-4434">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" onError={e => {
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80';
                  }} data-id="element-4435" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-4436">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-4437">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-0.5" data-id="element-4438">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-4439">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-4440">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                      <button onClick={() => removeItem(item.id)} className="text-gray-400 dark:text-[#7A8F8A] hover:text-red-500 dark:hover:text-red-400 transition-colors" aria-label={`Remove ${item.title}`} data-id="element-4441">
                        <TrashIcon className="h-4 w-4" data-id="element-4442" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:w-80" data-id="element-4443">
            <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm p-6 sticky top-24" data-id="element-4444">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-4445">
                Order Summary
              </h2>
              <div className="space-y-3 mb-4" data-id="element-4446">
                <div className="flex justify-between text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-4447">
                  <span data-id="element-4448">
                    Subtotal ({totalItems} item{totalItems !== 1 ? 's' : ''})
                  </span>
                  <span data-id="element-4449">${totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-4450">
                  <span data-id="element-4451">Processing fee</span>
                  <span data-id="element-4452">$0.00</span>
                </div>
              </div>
              <div className="border-t border-gray-100 dark:border-[#1A3330] pt-4 mb-6" data-id="element-4453">
                <div className="flex justify-between text-base font-semibold text-gray-900 dark:text-white" data-id="element-4454">
                  <span data-id="element-4455">Total</span>
                  <span data-id="element-4456">${totalPrice.toFixed(2)}</span>
                </div>
              </div>
              <button onClick={handleCompletePurchase} disabled={isProcessing} className={`w-full flex items-center justify-center px-6 py-3 rounded-lg text-base font-medium text-white bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] hover:shadow-lg transition-all ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-4457">
                {isProcessing ? <span className="flex items-center" data-id="element-4458">
                    <Loader2Icon className="animate-spin h-5 w-5 mr-2" data-id="element-4459" />
                    Processing your order...
                  </span> : 'Complete Purchase'}
              </button>
              <p className="mt-4 text-xs text-center text-gray-500 dark:text-[#7A8F8A]" data-id="element-4460">
                Secure checkout · All transactions encrypted
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-4461" />
    </div>;
};